import { html, unsafeCSS } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { WEB_COMPONENT_POLICY_PANEL_EXCHANGE_ANTIMALWARE } from '../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { GzToggleWebComponent } from '../../../../../../../ui/gzToggle';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import {
  ExchangeAntimalwareExclusionRule,
  ExchangeAntimalwareOnDemandScanCredentials,
  ExchangeAntimalwareRule,
  ExchangeAntimalwareScanTask,
  ExchangeAntimalwareSettings,
  ModuleId,
  SectionId,
} from '@nirvana/shared/dtos/types/policies/common';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import _, { cloneDeep } from 'lodash';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { toggleDisabled, toggleVisibility } from '../../../../../../../utils/htmlElement.utils';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import {
  ExchangeAntimalwareFormValue,
  ExchangeAntimalwareSearchableKeywords,
} from '../../../../utils/uiSettings/exchange/exchange-protection.types';
import {
  EXHANGE_ANTIMALWARE_DEFAULT_VALUE,
  EXHANGE_ANTIMALWARE_SETTINGS_DEFAULT_VALUE,
  getExchangeAntimalwareSearchableKeywords,
} from '../../../../utils/uiSettings/exchange/exchange-protection.values';
import { FontSizes, FontWeights } from '../../../../../../../types/common/types';
import { ButtonTypes } from '../../../../../../../ui/gzButton/types';
import {
  FullPagePopupConfigs,
  GzPolicyFullPageWindow,
} from '../../../../../../common/policyPanel/uiSettings/dialogs/fullPage/GzPolicyFullPageWindow';
import { GzExchangeAntimalwareSettingsFormWebComponent } from '../forms/settingsForm';
import { ValidationResult } from '../../../../../../../ui/gzForm/formField/types/form-validator';
import { GzHardCodedFormField } from '../../../../../../../ui/gzForm/formField/component/GzHardCodedFormField';
import { GzExchangeAntimalwareRulesGridWebComponent } from '../gridComponents/filteringRules/GzExchangeAntimalwareRulesGridWebComponent';
import { GzExchangeAntimalwareExclusionsGridWebComponent } from '../gridComponents/exclusions/GzExchangeAntimalwareExclusionsGridWebComponent';
import { GzExchangeAntimalwareScanTasksGridWebComponent } from '../gridComponents/scanTasks/GzExchangeAntimalwareScanTasksGridWebComponent';
import { GzExchangeAntimalwareOnDemandScanCredentialsFormWebComponent } from '../forms/onDemandScanCredentialsForm';

@customElement(WEB_COMPONENT_POLICY_PANEL_EXCHANGE_ANTIMALWARE)
export class GzExchangeAntimalwareWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  ExchangeAntimalwareFormValue,
  ExchangeAntimalwareSearchableKeywords
> {
  protected path = {
    moduleId: ModuleId.EXCHANGE_PROTECTION,
    sectionId: SectionId.EXCHANGE_ANTIMALWARE,
  };
  protected searchableKeywords = getExchangeAntimalwareSearchableKeywords();

  @query('#exchange-antimalware-enable-field')
  private antimalwareEnableField: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;
  @query('#exchange-antimalware-content')
  private antimalwareContent: HTMLElement;

  // this field is used to store the initial value of the antimalware settings
  private antimalwareSettingsField: GzHardCodedFormField<ExchangeAntimalwareSettings> =
    new GzHardCodedFormField<ExchangeAntimalwareSettings>(cloneDeep(EXHANGE_ANTIMALWARE_SETTINGS_DEFAULT_VALUE));
  @query('#exchange-antimalware-rules-field')
  private antimalwareRulesField: GzFormFieldWebComponent<
    GzExchangeAntimalwareRulesGridWebComponent,
    ExchangeAntimalwareRule[]
  >;
  @query('#exchange-antimalware-exclusions-field')
  private antimalwareExclusionsField: GzFormFieldWebComponent<
    GzExchangeAntimalwareExclusionsGridWebComponent,
    ExchangeAntimalwareExclusionRule[]
  >;

  // this field is used to store the initial value of the antimalware onDemandScanCredentials
  private antimalwareOnDemandScanCredentialsField: GzHardCodedFormField<
    ExchangeAntimalwareOnDemandScanCredentials | undefined
  > = new GzHardCodedFormField<ExchangeAntimalwareOnDemandScanCredentials | undefined>(undefined);
  @query('#exchange-antimalware-scan-tasks-title')
  private antimalwareScanTasksTitle: HTMLElement;
  @query('#exchange-antimalware-scan-tasks-description')
  private antimalwareScanTasksDescription: HTMLElement;
  @query('#exchange-antimalware-scan-tasks-warning')
  private antimalwareScanTasksWarning: HTMLElement;
  @query('#exchange-antimalware-scan-tasks-field')
  private antimalwareScanTasksField: GzFormFieldWebComponent<
    GzExchangeAntimalwareScanTasksGridWebComponent,
    ExchangeAntimalwareScanTask[]
  >;

  /**
   * Current accordion styles merged with template styles.
   */
  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  /**
   * Render specific title.
   */
  protected renderTitle() {
    return html` <gz-title
      class="content"
      searchable-label="${this.searchableKeywords[ExchangeAntimalwareSearchableKeywords.Antimalware].label}"
      ><span>${this.searchableKeywords[ExchangeAntimalwareSearchableKeywords.Antimalware].translatedLabel}</span>
    </gz-title>`;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html` <gz-text
      id="exchange-antimalware-content-text"
      fontSize="${FontSizes.PARAGRAPH_12}"
      fontWeight="${FontWeights.NORMAL}"
      class="infoText"
      >${translateFn(
        'The Antimalware module detects suspicious or infected objects and neutralizes them by disinfecting or isolating threats, safeguarding Exchange mail servers.'
      )}
    </gz-text>`;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`
      <gz-form-field id="exchange-antimalware-enable-field" class="mt-4 mbw-1" status-field>
        <gz-toggle
          class="content-title-toggle"
          searchable-label="${this.searchableKeywords[ExchangeAntimalwareSearchableKeywords.AntimalwareFiltering]
            .label}"
          gz-form-input
          @toggleChange="${this.onToggleChange}"
        >
          <span
            >${this.searchableKeywords[ExchangeAntimalwareSearchableKeywords.AntimalwareFiltering]
              .translatedLabel}</span
          >
        </gz-toggle>
      </gz-form-field>

      <div id="exchange-antimalware-content">
        <gz-text
          id="exchange-antimalware-content-filtering-text"
          fontSize="${FontSizes.PARAGRAPH_12}"
          fontWeight="${FontWeights.NORMAL}"
          class="infoText"
          >${translateFn(
            'Antimalware Filtering scans email traffic according to predefined rules and, if necessary, adds notes to inform users about actions taken.'
          )}
        </gz-text>

        <!-- exchange antimalware settings form -->
        <div id="exchange-antimalware-settings-content">
          <gz-title fontWeight="${FontWeights.SEMIBOLD}" fontSize="${FontSizes.PARAGRAPH_12}" class="mt-4 subtitle"
            >${translateFn('Email notes')}</gz-title
          >
          <gz-text
            id="exchange-antimalware-settings-info-text"
            fontSize="${FontSizes.PARAGRAPH_12}"
            fontWeight="${FontWeights.NORMAL}"
            class="infoText mt-1"
            >${translateFn('Edit the notes added to the email body after scanning.')}
          </gz-text>
          <gz-button
            type="${ButtonTypes.OUTLINE}"
            @click="${this.onEditSettingsButtonClick.bind(this)}"
            class="mt-2"
            searchable-label="${this.searchableKeywords[ExchangeAntimalwareSearchableKeywords.EditNotes].label}"
            >${this.searchableKeywords[ExchangeAntimalwareSearchableKeywords.EditNotes].translatedLabel}</gz-button
          >
        </div>

        <!-- exchange antimalware rules -->
        <div id="exchange-antimalware-rules-content">
          <gz-title fontWeight="${FontWeights.SEMIBOLD}" fontSize="${FontSizes.PARAGRAPH_12}" class="mt-4 subtitle"
            >${translateFn('Filtering rules')}</gz-title
          >
          <gz-text
            id="exchange-antimalware-rules-info-text"
            fontSize="${FontSizes.PARAGRAPH_12}"
            fontWeight="${FontWeights.NORMAL}"
            class="infoText mt-1"
            >${translateFn(
              'Create and configure rules to control the scanning process with predefined criteria and actions.'
            )}
          </gz-text>

          <gz-form-field id="exchange-antimalware-rules-field">
            <gz-policy-panel-exchange-antimalware-rules-grid
              class="removeGridPadding"
              gz-form-input
            ></gz-policy-panel-exchange-antimalware-rules-grid>
          </gz-form-field>
        </div>

        <!-- exchange antimalware exclusions -->
        <div id="exchange-antimalware-exclusions-content">
          <gz-title
            fontWeight="${FontWeights.SEMIBOLD}"
            fontSize="${FontSizes.PARAGRAPH_12}"
            class="mt-4 subtitle"
            searchable-label="${this.searchableKeywords[ExchangeAntimalwareSearchableKeywords.Exclusions].label}"
            >${this.searchableKeywords[ExchangeAntimalwareSearchableKeywords.Exclusions].translatedLabel}</gz-title
          >
          <gz-text
            id="exchange-antimalware-exclusions-info-text"
            fontSize="${FontSizes.PARAGRAPH_12}"
            fontWeight="${FontWeights.NORMAL}"
            class="infoText mt-1"
            >${translateFn('Create and configure exclusions to bypass specific emails during antimalware scanning.')}
          </gz-text>

          <gz-form-field id="exchange-antimalware-exclusions-field">
            <gz-policy-panel-exchange-antimalware-exclusions-grid
              class="removeGridPadding"
              gz-form-input
            ></gz-policy-panel-exchange-antimalware-exclusions-grid>
          </gz-form-field>
        </div>
      </div>

      <!-- exchange antimalware scan tasks -->
      <div id="exchange-antimalware-scan-tasks-content">
        <gz-title
          fontWeight="${FontWeights.MEDIUM}"
          fontSize="${FontSizes.BODY}"
          class="mt-4"
          searchable-label="${this.searchableKeywords[ExchangeAntimalwareSearchableKeywords.ScanTasks].label}"
          >${this.searchableKeywords[ExchangeAntimalwareSearchableKeywords.ScanTasks].translatedLabel}</gz-title
        >
        <gz-text
          id="exchange-antimalware-scan-tasks-info-text"
          fontSize="${FontSizes.PARAGRAPH_12}"
          fontWeight="${FontWeights.NORMAL}"
          class="infoText mt-1"
          >${translateFn(
            'The Antimalware module performs scheduled scans of Microsoft Exchange mailbox and public folder databases.'
          )}
        </gz-text>

        <div id="exchange-antimalware-scan-tasks-credentials-content">
          <gz-title
            fontWeight="${FontWeights.SEMIBOLD}"
            fontSize="${FontSizes.PARAGRAPH_12}"
            class="mt-4 subtitle"
            searchable-label="${this.searchableKeywords[ExchangeAntimalwareSearchableKeywords.OnDemandScanCredentials]
              .label}"
            >${this.searchableKeywords[ExchangeAntimalwareSearchableKeywords.OnDemandScanCredentials]
              .translatedLabel}</gz-title
          >
          <gz-text
            id="exchange-antimalware-scan-tasks-credentials-info-text"
            fontSize="${FontSizes.PARAGRAPH_12}"
            fontWeight="${FontWeights.NORMAL}"
            class="infoText mt-1"
            >${translateFn(
              'Exchange Protection leverages Microsoft Exchange Web Services (EWS) to scan mailboxes and public folders. An Exchange administrator account with impersonation rights is required for accessing and scanning user data. This account must be a member of the Organization Management group.'
            )}
          </gz-text>

          <gz-button
            id="exchange-antimalware-add-credentials-button"
            type="${ButtonTypes.OUTLINE}"
            @click="${this.onEditCredentialsButtonClick.bind(this)}"
            class="mt-3"
            >${translateFn('Add credentials')}</gz-button
          >
        </div>

        <div id="exchange-antimalware-scan-tasks-grid-content">
          <gz-title
            id="exchange-antimalware-scan-tasks-title"
            fontWeight="${FontWeights.SEMIBOLD}"
            fontSize="${FontSizes.PARAGRAPH_12}"
            class="mt-4 subtitle disabled"
            searchable-label="${this.searchableKeywords[ExchangeAntimalwareSearchableKeywords.ScanTasks].label}"
            >${this.searchableKeywords[ExchangeAntimalwareSearchableKeywords.ScanTasks].translatedLabel}</gz-title
          >

          <gz-text
            id="exchange-antimalware-scan-tasks-description"
            fontSize="${FontSizes.PARAGRAPH_12}"
            fontWeight="${FontWeights.NORMAL}"
            class="infoText mt-1 disabled"
            >${translateFn(
              'Configure Exchange scan tasks by specifying the targets and recurrence. The tasks become effective after the policy is saved.'
            )}
          </gz-text>

          <gz-info-box id="exchange-antimalware-scan-tasks-warning" severity="warning" class="hidden mt-3">
            ${translateFn('This section is enabled when valid credentials are provided.')}
          </gz-info-box>

          <gz-form-field id="exchange-antimalware-scan-tasks-field" class="disabled">
            <gz-policy-panel-exchange-antimalware-scan-tasks-grid
              class="removeGridPadding"
              gz-form-input
            ></gz-policy-panel-exchange-antimalware-scan-tasks-grid>
          </gz-form-field>
        </div>
      </div>
    `;
  }

  /**
   * Get data from manager and set missing fields.
   * Fields used in this section:
   * - uiSettings.exchange.antimalware
   */
  protected getValueFromManager(): ExchangeAntimalwareFormValue {
    // get default value
    const defaultValue = {
      uiSettings: {
        [ModuleId.EXCHANGE_PROTECTION]: { [SectionId.EXCHANGE_ANTIMALWARE]: EXHANGE_ANTIMALWARE_DEFAULT_VALUE },
      },
    };

    // get value from manager
    const value = {
      uiSettings: {
        [ModuleId.EXCHANGE_PROTECTION]: {
          [SectionId.EXCHANGE_ANTIMALWARE]: PolicyPanelManager.getInstance().getPolicy(
            'uiSettings.exchange.antimalware'
          ),
        },
      },
    };

    // merge received value with default one
    return deepmerge(defaultValue, value);
  }

  /**
   * Create form group.
   */
  protected createFormGroup() {
    this.formGroup = FormGroup.build({
      uiSettings: {
        exchange: {
          antimalware: {
            enable: this.antimalwareEnableField,
            settings: this.antimalwareSettingsField,
            rules: this.antimalwareRulesField,
            exclusions: this.antimalwareExclusionsField,
            onDemandScanCredentials: this.antimalwareOnDemandScanCredentialsField,
            scanTasks: this.antimalwareScanTasksField,
          },
        },
      },
    });
  }

  /**
   * Set form group value.
   */
  protected setFormData() {
    if (!this.valueT) {
      return;
    }

    // set form group value
    this.formGroup.value = {
      uiSettings: {
        exchange: {
          antimalware: this.valueT.uiSettings.exchange.antimalware,
        },
      },
    };

    // set on-demand scan credentials
    this.antimalwareOnDemandScanCredentialsField.value =
      this.valueT.uiSettings.exchange.antimalware.onDemandScanCredentials;
    this.toggleScanTasksSection(!this.valueT.uiSettings.exchange.antimalware.onDemandScanCredentials);
  }

  /**
   * Get form group value according to backend format.
   */
  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);

    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings', 'exchange', 'antimalware'], {});
    }

    return value;
  }

  /**
   * Enable/Disable module.
   * @param event
   */
  onToggleChange(event: CustomEvent) {
    toggleDisabled(this.antimalwareContent, !event.detail.checked);
  }

  private onEditSettingsButtonClick() {
    if (!this.valueT) {
      return;
    }

    const addRuleCallback = (formValue: ExchangeAntimalwareSettings): ValidationResult => {
      this.antimalwareSettingsField.value = formValue;
      return { valid: true };
    };

    // create configs
    const configs: FullPagePopupConfigs = {
      bodyForm: new GzExchangeAntimalwareSettingsFormWebComponent(this.antimalwareSettingsField.value),
      strings: {
        title: translateFn('Edit email notes'),
        confirm: translateFn('Save#preserve state'),
        cancel: translateFn('Cancel'),
      },
      confirmCallbackFn: addRuleCallback,
    };

    // show window
    const window = new GzPolicyFullPageWindow(configs);
    window.show();
  }

  private onEditCredentialsButtonClick() {
    if (!this.valueT) {
      return;
    }

    const editCredentialsCallback = (formValue: ExchangeAntimalwareOnDemandScanCredentials): ValidationResult => {
      this.antimalwareOnDemandScanCredentialsField.value = formValue;
      this.toggleScanTasksSection(false);
      return { valid: true };
    };

    // create configs
    const configs: FullPagePopupConfigs = {
      bodyForm: new GzExchangeAntimalwareOnDemandScanCredentialsFormWebComponent(
        this.antimalwareOnDemandScanCredentialsField.value
      ),
      strings: {
        title: this.antimalwareOnDemandScanCredentialsField.value
          ? translateFn('Edit credentials')
          : translateFn('Add credentials'),
        confirm: this.antimalwareOnDemandScanCredentialsField.value
          ? translateFn('Save#preserve state')
          : translateFn('Add'),
        cancel: translateFn('Cancel'),
      },
      confirmCallbackFn: editCredentialsCallback,
      isWindow: false,
    };

    // show window
    const window = new GzPolicyFullPageWindow(configs);
    window.show();
  }

  private toggleScanTasksSection(isDisabled: boolean) {
    toggleDisabled(this.antimalwareScanTasksTitle, isDisabled);
    toggleDisabled(this.antimalwareScanTasksDescription, isDisabled);
    toggleDisabled(this.antimalwareScanTasksField, isDisabled);
    toggleVisibility(this.antimalwareScanTasksWarning, isDisabled);
  }

  /**
   * Override reset method, because we need to reset also grid component.
   */
  public reset() {
    super.reset();

    // reset grids
    this.antimalwareRulesField.Input.reset();
    this.antimalwareExclusionsField.Input.reset();
    this.antimalwareScanTasksField.Input.reset();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_EXCHANGE_ANTIMALWARE: GzExchangeAntimalwareWebComponent;
  }
}
